import React, { useMemo, useState } from 'react';
import CascaDeModal from './shared/CascaDeModal';
import { SeletorDasListas, EfeitoDaSimulacao } from './ListasDoRateio';
import { rateioService } from '../services/api';
import { esquecerRateio, useRateio } from '../hooks/useRateio';
import { CriterioDeRateio, TEXTO_DO_CRITERIO } from '../utils/rateioDoGrupo';
import { CotasDivergentes, lerCotasDivergentes } from '../utils/cotasDivergentes';
import { SimulacaoDaDivisao } from '../utils/redividir';
import { ListaDoRateio, Selecao, candidatosDoGrupo, corpoDasListas, moverProduto, problemasDaSelecao } from '../utils/listasDoRateio';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { campo } from '../design';

/**
 * "COMEÇAR A DIVIDIR" (09/10/2026): a PRIMEIRA versão do rateio de um grupo que ainda não tem nenhuma. Até aqui a
 * tela de grupos não oferecia nada a um grupo sem rateio, e o primeiro só nascia por SQL (a CONTROLSUL, com a
 * FISCALMAX vinculada, foi o caso que bateu).
 *
 * O backend já cria na primeira gravação (`POST /clients/:id/rateio` sem linha em client_group_rateio é INSERT), com o
 * motor DESLIGADO por padrão. Sem versão anterior não há cota acordada, logo não há o 409 de regravação; o caminho de
 * corrida (alguém gravou no meio) ainda é lido e mostrado, como no editar.
 *
 * O fluxo é o do editar (EditarListasDoRateio), com as MESMAS peças: o seletor das duas listas e o efeito da simulação.
 * - Os produtos vêm das assinaturas ativas de TODAS as empresas do grupo, que é o que o backend mede no pool.
 * - O critério: por ramais (padrão) ou partes iguais. `percentual_fixo` não entra: ele LÊ os percentuais da versão
 *   anterior, e sem versão a soma não fecha.
 * - Simular → conferir a tabela por empresa → gravar, com o eco do pool e da impressão da simulação.
 */

const competenciaCorrente = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const CRITERIOS_DA_PRIMEIRA: CriterioDeRateio[] = ['ramais_contratados', 'partes_iguais'];

interface AssinaturaDoGrupo {
  clientId: number;
  status?: string | null;
  items?: Array<{ productId?: number | null; productCode?: string | null; name?: string | null; type?: string | null }> | null;
}

export default function ComecarADividir({ matrizId, empresas, contracts, nome, onClose, onGravado }: {
  matrizId: number;
  /** A matriz e as empresas ligadas a ela por `parentClientId`: o grupo como o backend o mede. */
  empresas: number[];
  contracts: AssinaturaDoGrupo[];
  nome: (clientId: number) => string;
  onClose: () => void;
  onGravado: () => void;
}) {
  const vazia: Selecao = useMemo(() => ({ divide: [], decide: [] }), []);
  const [selecao, setSelecao] = useState<Selecao>(vazia);
  const [criterio, setCriterio] = useState<CriterioDeRateio>('ramais_contratados');
  const [sim, setSim] = useState<SimulacaoDaDivisao | null>(null);
  // O que foi simulado: a seleção E o critério. Gravar manda exatamente isto.
  const [simulada, setSimulada] = useState<{ selecao: Selecao; criterio: CriterioDeRateio } | null>(null);
  const [erroDaSimulacao, setErroDaSimulacao] = useState<string | null>(null);
  const [competencia, setCompetencia] = useState(competenciaCorrente());
  const [observacao, setObservacao] = useState('');
  const [divergencia, setDivergencia] = useState<CotasDivergentes | null>(null);
  const [recalcular, setRecalcular] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const candidatos = useMemo(() => candidatosDoGrupo(contracts, empresas), [contracts, empresas.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const nomeDoProduto = useMemo(() => {
    const nomes = new Map(candidatos.map((c) => [c.productId, c.nome]));
    return (id: number) => nomes.get(id) || `produto #${id}`;
  }, [candidatos]);
  // Primeira versão: a lista do que se divide é livre (não há modelo do pool a respeitar ainda).
  const problemas = problemasDaSelecao(selecao, criterio, true, vazia);

  // Mudou a seleção ou o critério: a simulação na tela deixa de valer.
  const zerarSimulacao = () => { setSim(null); setSimulada(null); setDivergencia(null); setRecalcular(false); };
  const escolher = (productId: number, destino: ListaDoRateio | null) => { setSelecao((s) => moverProduto(s, productId, destino)); zerarSimulacao(); };
  const trocarCriterio = (c: CriterioDeRateio) => { setCriterio(c); zerarSimulacao(); };

  const simular = async () => {
    if (problemas.length) return;
    setErroDaSimulacao(null);
    setOcupado(true);
    try {
      const r = await rateioService.simularDivisao(matrizId, { criterio, ...corpoDasListas(selecao), descontosSeguemRateio: true });
      setSim(r);
      setSimulada({ selecao, criterio });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Não foi possível simular.';
      setErroDaSimulacao(Array.isArray(msg) ? msg.join(' ') : String(msg));
    } finally {
      setOcupado(false);
    }
  };

  const gravar = async () => {
    if (!sim || !simulada) return;
    setOcupado(true);
    setErro(null);
    try {
      await rateioService.salvar(
        matrizId,
        {
          competenciaInicio: competencia,
          poolParaConfirmar: sim.poolParaConfirmar,
          ...(sim.impressaoDaSimulacao ? { impressaoParaConfirmar: sim.impressaoDaSimulacao } : {}),
          criterio: simulada.criterio,
          ...corpoDasListas(simulada.selecao),
          descontosSeguemRateio: true,
          observacao: observacao.trim(),
          // ⚠️ NÃO É CAMPO MORTO. O pool desta tela é a soma de itens reais das assinaturas, então o modelo é
          // `composto` por construção. Hoje o backend não declara o modelo na criação e o `whitelist` descarta este
          // campo sem erro (forbidNonWhitelisted: false); no dia em que o GravarRateioDto o aceitar na criação, ele
          // passa a valer sem mudar a tela. Sem ele declarado, trocar as listas depois é recusado (409
          // modelo_do_pool_nao_declarado).
          modeloDoPool: 'composto',
        },
        { recalcularCotas: !!divergencia && recalcular },
      );
      esquecerRateio();
      onGravado();
    } catch (e: any) {
      const d = lerCotasDivergentes(e);
      if (d) { setDivergencia(d); setRecalcular(false); }
      else {
        const msg = e?.response?.data?.message || e?.response?.data?.mensagem || e?.message || 'Erro ao gravar';
        setErro(Array.isArray(msg) ? msg.join(' ') : String(msg));
        if (/base mudou desde a simula|conjunto que esta sendo gravado nao e o que foi simulado/i.test(String(msg))) { setSim(null); setSimulada(null); }
      }
    } finally {
      setOcupado(false);
    }
  };

  // A simulação com UMA empresa só: não há o que dividir, e gravar daria 100% a ela. A porta já filtra isso; aqui é o
  // caso que escapa por outro caminho (a assinatura de uma empresa caiu entre a carga da tela e a simulação).
  const umaEmpresaSo = !!sim && sim.linhas.length < 2;
  const bloqueado = ocupado || !sim || !sim.somaFecha || umaEmpresaSo || !competencia || !observacao.trim() || (!!divergencia && !recalcular);

  return (
    <CascaDeModal
      largura='lg'
      titulo='Começar a dividir'
      subtitulo={`grupo de ${nome(matrizId)} · primeira divisão`}
      onClose={onClose}
      corpoClassName='p-5 space-y-4'
      rodape={
        <>
          <span className='text-xs text-slate-500'>Grava a primeira versão do rateio. O motor fica desligado.</span>
          <div className='ml-auto flex gap-2'>
            <button type='button' onClick={onClose} className='px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white'>Cancelar</button>
            {!sim ? (
              <button type='button' onClick={simular} disabled={ocupado || problemas.length > 0} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50'>
                {ocupado ? 'Simulando…' : 'Simular a divisão'}
              </button>
            ) : (
              <button type='button' onClick={gravar} disabled={bloqueado} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50'>
                {ocupado ? 'Gravando…' : 'Gravar a divisão'}
              </button>
            )}
          </div>
        </>
      }
    >
      <p className='text-sm text-slate-400'>
        Escolha o que o grupo usa junto (o que se divide) e o que decide quanto cada empresa paga. A simulação mostra a cota de cada empresa antes de gravar.
      </p>

      {/* 1. O que se divide e o que decide: o MESMO seletor do editar. */}
      {candidatos.length === 0 ? (
        <p className={`text-sm rounded-lg border px-3 py-2 ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`} data-testid='sem-produtos'>
          Nenhuma empresa do grupo tem assinatura ativa com produto recorrente: não há o que dividir.
        </p>
      ) : (
        <SeletorDasListas linhas={candidatos} selecao={selecao} original={vazia} podeEditarOQueSeDivide naoSeEdita={null} problemas={problemas} nome={nome} onEscolher={escolher} />
      )}

      {/* 2. O critério. */}
      <div>
        <p className='text-xs font-medium text-slate-400 mb-1'>Como dividir</p>
        <div className='flex gap-2' role='radiogroup' aria-label='Critério'>
          {CRITERIOS_DA_PRIMEIRA.map((c) => (
            <button
              key={c}
              type='button'
              role='radio'
              aria-checked={criterio === c}
              onClick={() => trocarCriterio(c)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${criterio === c ? 'border-slate-400 text-white font-medium' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
            >
              {TEXTO_DO_CRITERIO[c]}
            </button>
          ))}
        </div>
      </div>

      {erroDaSimulacao && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>{erroDaSimulacao}</div>}
      {erro && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>{erro}</div>}

      {/* 3. O efeito, como medição: o MESMO efeito do editar. */}
      {umaEmpresaSo && (
        <p role='alert' className={`text-sm rounded-lg border px-3 py-2 ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`} data-testid='uma-empresa-so'>
          Só uma empresa tem assinatura ativa; não há o que dividir.
        </p>
      )}
      {sim && <EfeitoDaSimulacao sim={sim} nome={nome} nomeDoProduto={nomeDoProduto} divergencia={divergencia} recalcular={recalcular} onRecalcular={setRecalcular} />}

      {/* 4. A competência e a observação. */}
      <div className='grid grid-cols-3 gap-3'>
        <div>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='cd-comp'>Vale a partir de *</label>
          <input id='cd-comp' type='month' value={competencia} onChange={(e) => setCompetencia(e.target.value)} className={`w-full px-2 py-1.5 rounded-md text-sm ${campo}`} />
        </div>
        <div className='col-span-2'>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='cd-obs'>Observação * <span className='text-slate-500'>(o acordo do grupo)</span></label>
          <textarea id='cd-obs' rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} />
        </div>
      </div>
      <div className='space-y-1.5 text-xs text-slate-400' data-testid='depois-de-gravar'>
        <p className='font-medium text-slate-300'>Gravar não muda fatura nenhuma.</p>
        <p>O motor do rateio nasce desligado: a geração de fatura não consulta a divisão até alguém simular o motor (o antes e o depois de cada fatura) e ligá-lo, no painel do motor que aparece no grupo depois de gravar.</p>
        <p className={`rounded-lg border px-3 py-2 ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`} data-testid='listas-congeladas'>
          As listas ficam congeladas até o modelo do pool ser declarado; para mudar o que se divide depois, é pelo backend. Redividir com as mesmas listas continua livre.
        </p>
      </div>
    </CascaDeModal>
  );
}

/**
 * A PORTA, na tela de grupos: só no grupo cuja leitura do rateio diz que NÃO há rateio (`temRateio === false`), só
 * para admin, e só com duas empresas ou mais. Leitura carregando ou com erro não oferece nada: "sem rateio" tem de
 * ter sido LIDO, não suposto.
 */
export function PortaDoComecarADividir({ clientId, empresas, contracts, nome, podeEditar, onGravado }: {
  clientId: number;
  empresas: number[];
  contracts: AssinaturaDoGrupo[];
  nome: (clientId: number) => string;
  podeEditar: boolean;
  onGravado: () => void;
}) {
  const { rateio } = useRateio(clientId);
  const [aberto, setAberto] = useState(false);
  // ⛔ O backend só divide entre empresas com assinatura ATIVA (medirGrupo). A FESTVAL tem 37 lojas ligadas, todas
  // cobradas na fatura da matriz: contar só o vínculo oferecia uma "divisão" que dava 100% à matriz (prévia viva de
  // 09/10/2026). A porta conta quem o backend divide.
  const comAssinatura = empresas.filter((id) => contracts.some((c) => c.clientId === id && c.status === 'Ativo'));
  if (!podeEditar || !rateio || rateio.temRateio !== false || comAssinatura.length < 2) return null;
  return (
    <div className='flex items-center gap-3 rounded-lg border border-slate-700 px-3 py-2' data-testid='porta-comecar-a-dividir'>
      <p className='text-sm text-slate-400 flex-1'>Este grupo ainda não divide nada entre as empresas.</p>
      <button type='button' onClick={() => setAberto(true)} className='text-xs px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200'>
        Começar a dividir
      </button>
      {aberto && (
        <ComecarADividir
          matrizId={rateio.matrizId}
          empresas={empresas}
          contracts={contracts}
          nome={nome}
          onClose={() => setAberto(false)}
          onGravado={() => { setAberto(false); onGravado(); }}
        />
      )}
    </div>
  );
}
